import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { SurveyType } from './types';
import { TRACKING_VISITED_SOLICIT_FEEDBACK } from '../OnboardingForm/constants';
import { useSendFeedback } from '../../api/scheduleAppointment/useSendFeedback';
import { CareModel, MeetingTypeId } from '../../api/types';
import { useUserPublicInfo } from '../../api/public/useUserPublicInfo';
import { useNavigateWithParams } from '../../hooks/useNavigateWithParams';
import { useTracking } from '../../hooks/useTracking';
import { useReadAuthUserIdWithQueryParamsLookup } from '../../hooks/useReadAuthUserIdWithQueryParamsLookup';
import { Meeting } from '../../models/meeting';
import usePatchPublicUser from 'api/public/mutations/usePatchPublicUser';

export const useFeedbackPage = () => {
  const { page: pageView } = useTracking();
  const { state }: { state?: { userEmail?: string; bookedMeeting?: Meeting } } =
    useLocation();
  const { userEmail, bookedMeeting } = state || {};
  const { userId } = useReadAuthUserIdWithQueryParamsLookup();
  const { data: userPublicData, isLoading: loadingUserPublicData } =
    useUserPublicInfo(userId, true);
  const navigate = useNavigateWithParams();
  const { mutation: sendFeedbackMutation } =
    useSendFeedback<SurveyType.visitGoals>();
  const { mutation: updateUserMutation } = usePatchPublicUser();
  const [feedbackTxt, setFeedbackTxt] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [agreeChecked, setAgreeChecked] = useState(false);

  const collectPhoneNumber =
    !loadingUserPublicData && !userPublicData?.phone_number_provided;
  const feedbackValid = !!feedbackTxt;
  const phoneValid = phoneNumber?.length === 12 && agreeChecked;

  useEffect(() => {
    if (!userId) return;

    pageView(TRACKING_VISITED_SOLICIT_FEEDBACK);
  }, [pageView, userId]);

  const appointment_type =
    userPublicData?.care_model === CareModel.Collaborative
      ? MeetingTypeId.InitialConsultation
      : MeetingTypeId.InitialCoachCall;

  const sendFeedback = async () => {
    if (!userId) return;

    try {
      await sendFeedbackMutation.mutateAsync({
        survey: {
          goals: feedbackTxt,
          appointment_type,
        },
        survey_type: SurveyType.visitGoals,
        user_id: userId,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const updateUser = async () => {
    if (!userId) return;

    try {
      await updateUserMutation.mutateAsync({
        userId,
        payload: { phone_number: phoneNumber },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (!userId) return;

    const pendingRequests: Promise<unknown>[] = [];

    if (collectPhoneNumber && phoneValid) {
      pendingRequests.push(updateUser());
    }

    if (feedbackValid) {
      pendingRequests.push(sendFeedback());
    }

    await Promise.allSettled(pendingRequests);

    navigate('/thank-you', { state: { userEmail, bookedMeeting } });
  };

  const onSkip = () => {
    navigate('/thank-you', { state: { userEmail, bookedMeeting } });
  };

  return {
    bookedMeeting,
    feedbackTxt,
    isLoading:
      loadingUserPublicData ||
      sendFeedbackMutation.isPending ||
      updateUserMutation.isPending,
    setFeedbackTxt,
    onSkip,
    redirectToHome: !userId,
    timezone: userPublicData?.timezone,
    phoneNumber,
    agreeChecked,
    setPhoneNumber,
    setAgreeChecked,
    phoneValid,
    feedbackValid,
    collectPhoneNumber,
    handleSubmit,
  };
};
